import React, { useEffect, useState } from "react";
import { useDispatch, connect } from "react-redux";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardHeader,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Dropdown,
} from "reactstrap";
import {
  Table, Thead, Tbody, Tr, Th, Td 
} from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { withTranslation } from "react-i18next";
import CustomPagination from "components/Common/CustomPagination";
import TableLoader from "components/Common/TableLoader";
import SearchBar from "components/Common/SearchBar";
import ClientFilter from "../Clients/ClientFilter";
import { Link, useLocation } from "react-router-dom";
import { captilazeFirstLetter } from "common/utils/manipulateString";
import { checkAllBoxes } from "common/utils/checkAllBoxes";
import AgentForm from "../Clients/AgentDropDown";
import formatDate from "helpers/formatDate";
import CallStatusDropdown from "components/Common/CallStatusDropdown";
import ToolTipData from "components/Common/ToolTipData";
import FeatherIcon from "feather-icons-react";
import { fetchClientsStart } from "store/client/actions";

const BaseClientsList = (props) => {
  const { t, filterKey = "isActive", extraActions, extraModals } = props;
  const search = useLocation().search;
  const query = new URLSearchParams(search);
  const [assignedClients, setAssignedClients] = useState([]);
  const [sizePerPage, setSizePerPage] = useState(50);
  const [searchInputText, setSearchInputText] = useState("");
  const [filteredValues, setFilteredValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    gender: "",
    country: "",
    filterDate: {
      fromDate: query.get("fromDate") || "",
      toDate: "",
    },
    type: "",
    source: "",
    agent: "",
    kyc: query.get("kyc") || "",
    assigne: query.get("assigne") || "",
    callStatus: query.get("callStatus") || "",
    login: "",
    categories: "",
    parentId: "",
  });
  const [headers, setHeaders] = useState(false);
  const baseColumns = [
    {
      dataField: "checkbox",
      text: (
        <div className="d-flex align-items-center justify-content-center">
          <input
            type="checkbox"
            id="select-all-clients"
            onChange={(e) => {
              checkAllBoxes("select-all-clients", ".client-checkbox");
              setAssignedClients(e.target.checked ? [...props.clients] : []);
            }}
          />
        </div>
      ),
      formatter: (val) => (
        <div className="d-flex align-items-center justify-content-center">
          <input
            type="checkbox"
            onChange={(e) =>
              setAssignedClients((prev) =>
                e.target.checked
                  ? [val, ...prev]
                  : prev.filter((client) => client._id !== val._id)
              )
            }
            className="client-checkbox"
          />
        </div>
      ),
    },
    {
      dataField: "createdAt",
      text: t("Date"),
      formatter: (val) => formatDate(val.createdAt, "DD/MM/YYYY"),
    },
    {
      dataField: "name",
      text: t("Name"),
      formatter: (client) => (
        <div>
          <Link
            to={{
              pathname: "/clients/" + client._id + "/profile",
              state: { clientId: client._id },
            }}
          >
            <strong className="text-capitalize">{`${client.firstName} ${client.lastName || ""}`}</strong>
          </Link>
        </div>
      ),
    },
    {
      dataField: "mt5",
      text: t("FX Account"),
      formatter: (val) => {
        if (val?.fx?.isIb) {
          if (val?.fx?.ibMT5Acc) return val.fx.ibMT5Acc[0];
          else if (val?.fx?.ibMT4Acc) return val.fx.ibMT4Acc[0];
        }
        if (val?.fx?.liveAcc) return val.fx.liveAcc[0];
        else if (val?.fx?.demoAcc) return val.fx.demoAcc[0];
      },
    },
    {
      dataField: "Category",
      text: t("Category"),
      formatter: (val) => {
        let categories = [];
        if (val?.fx?.isClient) {
          categories.push(val?.isCorporate ? "Corporate" : "Individual");
        }
        if (val?.fx?.isIb) {
          categories.push("ib");
        }
        return categories.toString();
      },
    },
    {
      dataField: "email",
      text: t("Email"),
      formatter: (val, index) => (
        <>
          <span id={`ClientEmailToolTip_${index}`}>{val.email}</span>
          <ToolTipData target={`ClientEmailToolTip_${index}`} placement="top" data={val.email} />
        </>
      ),
    },
    {
      dataField: "phone",
      text: t("Phone"),
      formatter: (val, index) =>
        val.phone ? (
          <>
            <span id={`ClientPhoneToolTip_${index}`}>{val.phone}</span>
            <ToolTipData target={`ClientPhoneToolTip_${index}`} placement="top" data={val.phone} />
          </>
        ) : (
          "-"
        ),
    },
    {
      dataField: "callStatus",
      text: t("Status"),
      formatter: (val) => <CallStatusDropdown client={val} />,
    },
    {
      dataField: "country",
      text: t("Country"),
      formatter: (val, index) =>
        val && val.country ? (
          <>
            <span id={`ClientCountryToolTip_${index}`}>{captilazeFirstLetter(val.country)}</span>
            <ToolTipData target={`ClientCountryToolTip_${index}`} placement="top" data={captilazeFirstLetter(val.country)} />
          </>
        ) : (
          "-"
        ),
    },
    {
      dataField: "agent",
      isDummyField: true,
      editable: false,
      text: t("Agent"),
      formatter: (val) =>
        val && val.agent ? captilazeFirstLetter(`${val.agent.firstName} ${val.agent.lastName}`) : "Unassigned",
    },
    {
      dataField: "ib",
      text: t("IB"),
      formatter: (val) =>
        val?.parentId ? (
          <Link
            to={{
              pathname: "/clients/" + val.parentId._id + "/profile",
              state: { valId: val.parentId._id },
            }}
          >
            {captilazeFirstLetter(`${val.parentId.firstName} ${val.parentId.lastName}`)}
          </Link>
        ) : (
          "-"
        ),
    },
    {
      dataField: "source",
      text: t("Source"),
      formatter: (val) => (val && val.source === "REGISTER_DEMO" ? "Register Demo" : val.source),
    },
    {
      dataField: "stages",
      text: t("KYC"),
      formatter: (val) => {
        if (val.stages) {
          const { kycApproved, kycRejected } = val.stages;
          if (kycApproved) {
            return (
              <div>
                <Link className="text-success" to="#">
                  <i className="mdi mdi-check-circle font-size-20" />
                </Link>
              </div>
            );
          }
          if (kycRejected) {
            return (
              <div>
                <Link className="text-danger" to="#">
                  <i className="mdi mdi-close-thick font-size-20" />
                </Link>
              </div>
            );
          }
          return (
            <div>
              <Link className="text-warning" to="#">
                <i className="mdi mdi-alert-decagram-outline font-size-20" />
              </Link>
            </div>
          );
        } else {
          return "Pending";
        }
      },
    },
  ];
  const [columns, setColumns] = useState([...baseColumns]);
  const dispatch = useDispatch();
  const handleSearchInput = (e) => setSearchInputText(e.target.value);
  const loadClients = (page, limit) => {
    const filter = {
      ...filteredValues,
      [filterKey]: true 
    };
    if (searchInputText && searchInputText.length >= 3) {
      const searchText = searchInputText.split("@").shift();
      dispatch(fetchClientsStart({
        limit,
        page,
        searchText,
        filteredValues: filter 
      }));
    } else {
      dispatch(fetchClientsStart({
        limit,
        page,
        filteredValues: filter 
      }));
    }
  };
  useEffect(() => {
    loadClients(1, sizePerPage);
  }, [sizePerPage, searchInputText, filteredValues, props.assignAgent?.success]);
  const filterChangeHandler = (newFilteredValues) => {
    setFilteredValues(newFilteredValues);
  };

  const title = t(filterKey === "isArchive" ? "Archived Clients List" : "Clients List");
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          {extraModals}
          <h2>{title}</h2>
          <Row>
            <Col className="col-12">
              <Card>
                <CardHeader className="d-flex flex-column gap-3">
                  <div className="d-flex flex-row justify-content-between align-items-center">
                    <CardTitle className="color-primary">
                      <h5>
                        {title} ({props.totalDocs}){" "}
                        <FeatherIcon icon="refresh-cw" className="icon-lg ms-2" style={{ cursor: "pointer" }} onClick={() => loadClients(1, sizePerPage)} />
                      </h5>
                    </CardTitle>
                  </div>
                  <div className="d-flex flex-column flex-md-row gap-2 align-items-center justify-content-between">
                    <div className="d-flex flex-row align-items-center gap-2">
                      <SearchBar handleSearchInput={handleSearchInput} />
                      <ClientFilter filterChangeHandler={filterChangeHandler} filteredValues={filteredValues} />
                    </div>
                    <div className="d-flex flex-row align-items-center justify-content-between gap-2">
                      {props.clientPermissions.create && assignedClients.length > 0 && (
                        <AgentForm clients={[...assignedClients]} />
                      )}
                      {extraActions}
                      <Dropdown color="primary" isOpen={headers} toggle={() => setHeaders(!headers)}>
                        <DropdownToggle caret color="primary" className="btn btn-primary" size="md">
                          {t("Headers")}
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem header>Header</DropdownItem>
                          {baseColumns
                            .filter((c) => c.dataField !== "checkbox")
                            .map((column, index) => (
                              <DropdownItem
                                key={index}
                                onClick={() => {
                                  if (columns.find((c) => c.dataField === column.dataField)) {
                                    setColumns(columns.filter((c) => c.dataField !== column.dataField));
                                  } else {
                                    const idx = baseColumns.findIndex((c) => c.dataField === column.dataField);
                                    setColumns([...columns.slice(0, idx), column, ...columns.slice(idx)]);
                                  }
                                }}
                              >
                                {columns.find((c) => c.dataField === column.dataField) ? (
                                  <i className="mdi mdi-check me-1" />
                                ) : (
                                  <i className="mdi mdi-close me-1" />
                                )}
                                {column.text}
                              </DropdownItem>
                            ))}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="table-rep-plugin">
                    <div className="table-responsive mb-0" data-pattern="priority-columns">
                      <Table id="tech-companies-1" className="table table-hover table-clients fs-7">
                        <Thead className="text-center table-light">
                          <Tr>
                            {columns.map((column, index) => (
                              <Th data-priority={index} key={index}>
                                <span className="color-primary">{column.text}</span>
                              </Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody className="text-center fs-8" style={{ fontSize: "13px" }}>
                          {props.loading && <TableLoader colSpan={4} />}
                          {!props.loading &&
                                                        props.clients.map((row, rowIndex) => (
                                                          <Tr key={rowIndex} style={{ overflow: "visible" }}>
                                                            {columns.map((column, colIndex) => (
                                                              <Td
                                                                key={`${rowIndex}-${colIndex}`}
                                                                style={{
                                                                  overflow: column.dataField === "actions" && "visible",
                                                                  maxWidth: column.dataField === "actions" && "140px",
                                                                }}
                                                              >
                                                                {column.formatter ? column.formatter(row, rowIndex) : row[column.dataField]}
                                                              </Td>
                                                            ))}
                                                          </Tr>
                                                        ))}
                        </Tbody>
                      </Table>
                      <CustomPagination {...props} setSizePerPage={setSizePerPage} sizePerPage={sizePerPage} onChange={loadClients} docs={props.clients} />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  loading: state.clientReducer.loading || false,
  clients: state.clientReducer.clients || [],
  page: state.clientReducer.page || 1,
  totalDocs: state.clientReducer.totalDocs || 0,
  totalPages: state.clientReducer.totalPages || 0,
  hasNextPage: state.clientReducer.hasNextPage,
  hasPrevPage: state.clientReducer.hasPrevPage,
  limit: state.clientReducer.limit,
  nextPage: state.clientReducer.nextPage,
  pagingCounter: state.clientReducer.pagingCounter,
  prevPage: state.clientReducer.prevPage,
  clientPermissions: state.Profile.clientPermissions || {},
  assignAgent: state.usersReducer.assignAgent,
});

export default connect(mapStateToProps, null)(withTranslation()(BaseClientsList));